import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { io, type Socket } from 'socket.io-client';

export type RealtimeChannel =
  | 'articles'
  | 'article-categories'
  | 'offers'
  | 'offer-categories'
  | 'directory'
  | 'directory-categories'
  | 'businesses'
  | 'business-types'
  | 'formulas'
  | 'reviews'
  | 'appointments'
  | 'appointment-reasons'
  | 'cities'
  | 'countries'
  | 'me'
  | 'weekly-tips'
  | 'notifications'
  | 'weeks';

type Versions = Record<RealtimeChannel, number>;

const INITIAL: Versions = {
  'articles': 0, 'article-categories': 0,
  'offers': 0, 'offer-categories': 0,
  'directory': 0, 'directory-categories': 0,
  'businesses': 0, 'business-types': 0,
  'formulas': 0, 'reviews': 0,
  'appointments': 0, 'appointment-reasons': 0,
  'cities': 0, 'countries': 0,
  'me': 0, 'weekly-tips': 0, 'notifications': 0, 'weeks': 0,
};

const RealtimeContext = createContext<Versions>(INITIAL);

export function useRealtimeVersion(channel: RealtimeChannel): number {
  return useContext(RealtimeContext)[channel];
}

export function RealtimeProvider({ children }: { children: ReactNode }): JSX.Element {
  const [versions, setVersions] = useState<Versions>(INITIAL);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    const socket = io(import.meta.env['VITE_API_BASE_URL'] ?? 'http://localhost:3001', {
      path: '/ws',
      transports: ['websocket'],
    });
    socketRef.current = socket;

    socket.on('connect', () => console.log('[realtime] connected', socket.id));
    socket.on('disconnect', (reason) => console.log('[realtime] disconnected', reason));
    socket.on('connect_error', (err) => console.error('[realtime] connect_error', err.message));

    const bump = (channel: RealtimeChannel) => {
      console.log('[realtime] event', channel);
      setVersions((v) => ({ ...v, [channel]: v[channel] + 1 }));
    };

    socket.on('invalidate:articles',            () => bump('articles'));
    socket.on('invalidate:article-categories',  () => bump('article-categories'));
    socket.on('invalidate:offers',              () => bump('offers'));
    socket.on('invalidate:offer-categories',    () => bump('offer-categories'));
    socket.on('invalidate:directory',           () => bump('directory'));
    socket.on('invalidate:directory-categories',() => bump('directory-categories'));
    socket.on('invalidate:businesses',          () => bump('businesses'));
    socket.on('invalidate:business-types',      () => bump('business-types'));
    socket.on('invalidate:formulas',            () => bump('formulas'));
    socket.on('invalidate:reviews',             () => bump('reviews'));
    socket.on('invalidate:appointments',        () => bump('appointments'));
    socket.on('invalidate:appointment-reasons', () => bump('appointment-reasons'));
    socket.on('invalidate:cities',              () => bump('cities'));
    socket.on('invalidate:countries',           () => bump('countries'));
    socket.on('invalidate:me',                  () => bump('me'));
    socket.on('invalidate:weekly-tips',         () => bump('weekly-tips'));
    socket.on('invalidate:notifications',       () => bump('notifications'));
    socket.on('invalidate:weeks',               () => bump('weeks'));

    return () => { socket.disconnect(); };
  }, []);

  return <RealtimeContext.Provider value={versions}>{children}</RealtimeContext.Provider>;
}
